import { computed, onMounted, onUnmounted, getCurrentInstance, ref, watch } from 'vue';
import { QueryValueDecoder, QueryValueEncoder } from '@/ui/routes/query';
import { router } from '../routes';
import * as TE from 'fp-ts/lib/TaskEither';
import { pipe } from 'fp-ts/pipeable';

/**
 * THIS IS GLOBAL STATE FOR ALL QUERY VARIABLES
 *
 * This is where we store the current/next pushed query string
 * for all query state variables
 */
let timeout: number;
let query: typeof router.currentRoute.query = {};
let queryInitialized = false;
const debounceTime = 500;

const pushQuery = async () => {
  //router.push throws NavigationDuplicated
  await pipe(
    TE.tryCatch(
      () => router.push({ query }),
      reason => new Error(`${reason}`),
    ),
    TE.mapLeft(error => console.warn(error.message)),
  )();
};

// TODO: There must be a better way of deferring reactivity and updating multiple computed variables
//  on one go. Until we find it, this global flag can be used to hold off on updating the querystring
let queryStateEnabled = true;

/**
 * Update multiple queryState variables in one go
 * @param cb Callback function to do the mutation
 * @param debounce debounce route push or not
 */
export function updateMultiple(cb: Function, debounce = false) {
  queryStateEnabled = false;
  cb();
  queryStateEnabled = true;

  if (debounce) {
    window.clearTimeout(timeout);
    timeout = window.setTimeout(() => pushQuery(), debounceTime);
  } else {
    window.clearTimeout(timeout);
    pushQuery();
  }
}

export function queryState<T>(
  key: string,
  encode: QueryValueEncoder<T>,
  decode: QueryValueDecoder<T>,
  encodedDefaultValue: string,
  debounce = true,
) {
  const instance = getCurrentInstance();
  const queryValue = ref(instance?.proxy?.$route?.query?.[key]);

  watch(() => instance?.proxy?.$route?.query?.[key], value => {
    queryValue.value = value;
  });

  /**
   * With these hooks we make sure that all route component using query state starts with
   * the current query state variables inside 'query', and then starts modifying it.
   * The queryInitialized flag is needed to only do the setting once.
   */
  onMounted(() => {
    if (!queryInitialized) {
      queryInitialized = true;
      query = { ...getCurrentInstance()?.proxy?.$route?.query };
    }
  });
  onUnmounted(() => {
    if (queryInitialized) {
      queryInitialized = false;
    }
  });

  const state = computed<T>({
    // Get value from query string. Returns default value if value is null
    // (key is set but value is not) or undefined (key is not set)
    get: () => {
      const { value } = queryValue;

      if (typeof value !== 'string') {
        return decode(encodedDefaultValue);
      }
      return decode(value);
    },

    // Set value to query string by pushing new route, debounced when needed
    set: value => {
      delete query[key];
      const encodedValue = encode(value);
      if (encodedValue !== encodedDefaultValue) {
        query[key] = encodedValue;
      }

      if (!queryStateEnabled) {
        return;
      }

      if (debounce) {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(() => pushQuery(), debounceTime);
      } else {
        window.clearTimeout(timeout);
        pushQuery();
      }
    },
  });

  return state;
}
